import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, BarChart3, Share2, Copy } from "lucide-react";
import { api, track } from "@/lib/api";
import { toast } from "sonner";

function MonthlyWrappedCard({ data, onShare }) {
  const st = data.stats; const r = data.report;
  const rows = [
    ["You completed", `${st.missions} missions`], ["You built", `${st.proofs} career proofs`],
    ["Career Readiness", `${st.readiness_from}% → ${st.readiness}%`, true],
    ["Time on your career", `${Math.floor(st.time_spent_min / 60)}h ${st.time_spent_min % 60}m`],
    ["Biggest achievement", r.accomplished], ["Next month focus", r.next_month_focus],
  ];
  return (
    <div className={`overflow-hidden rounded-[28px] bg-gradient-to-br ${data.color} p-6 text-white shadow-2xl`} data-testid="monthly-wrapped-card">
      <div className="flex items-center justify-between"><span className="font-display text-lg font-extrabold">Nx<span className="text-amber-300">T</span></span><span className="font-mono text-xs text-white/60">{data.month}</span></div>
      <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-white/60">My {data.month.split(" ")[0]} Career Wrapped</div>
      <div className="mt-4 space-y-3">
        {rows.map(([l, v, big]) => (
          <div key={l}><div className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/50">{l}</div><div className={`font-display font-extrabold leading-tight ${big ? "text-3xl text-amber-300" : "text-base"}`}>{v}</div></div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button onClick={() => onShare("whatsapp")} data-testid="monthly-share-whatsapp" className="flex items-center justify-center gap-1.5 rounded-full bg-white/15 py-2.5 text-xs font-bold hover:bg-white/25"><Share2 className="h-3.5 w-3.5" /> Share</button>
        <button onClick={() => onShare("copy")} data-testid="monthly-share-copy" className="flex items-center justify-center gap-1.5 rounded-full bg-white/15 py-2.5 text-xs font-bold hover:bg-white/25"><Copy className="h-3.5 w-3.5" /> Copy</button>
      </div>
    </div>
  );
}

export default function Report() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { api.get("/monthly-report").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;
  if (!data) return <div className="py-20 text-center text-muted-foreground">Couldn't load your report. Try again.</div>;

  const r = data.report;
  const share = async (channel) => {
    const st = data.stats;
    const text = `My ${data.month} Career Wrapped on NxT: ${st.missions} missions, ${st.proofs} career proofs, readiness ${st.readiness_from}% → ${st.readiness}%. Next: ${r.next_month_focus}. Discover your Career DNA 👇 ${window.location.origin}`;
    track("career_wrapped_shared", { channel, monthly: true });
    api.post("/wrapped/share", { channel: `monthly_${channel}` }).catch(() => {});
    if (channel === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    else { await navigator.clipboard.writeText(text); toast.success("Copied!"); }
  };
  const sections = [
    ["What you accomplished", r.accomplished], ["What improved", r.improved], ["What you're avoiding", r.avoiding],
    ["Your strongest skill", r.strongest_skill], ["Your weakest skill", r.weakest_skill],
  ];

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/app/career")} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div><div className="flex items-center gap-2 text-indigo-600"><BarChart3 className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-widest">Monthly Report</span></div><h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight">{data.month}</h1></div>

      <MonthlyWrappedCard data={data} onShare={share} />

      <div className="grid grid-cols-3 gap-3">
        {[["Missions", data.stats.missions], ["Readiness", `${data.stats.readiness}%`], ["Streak", `${data.stats.streak}d`]].map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-border bg-card p-4 text-center"><div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{k}</div><div className="font-display text-2xl font-extrabold">{v}</div></div>
        ))}
      </div>

      {sections.map(([title, body], i) => (
        <motion.div key={title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
          className="rounded-3xl border border-border bg-card p-5"><div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{title}</div><p className="mt-1 font-medium">{body}</p></motion.div>
      ))}
      <div className="rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-600 p-6 text-white">
        <div className="text-xs font-bold uppercase tracking-widest text-white/60">Your next month focus</div>
        <div className="mt-1 font-display text-2xl font-extrabold">{r.next_month_focus}</div>
        <div className="mt-3 text-sm text-white/80">Recommended quest: <span className="font-bold">{r.recommended_quest}</span></div>
      </div>
    </div>
  );
}
